import type { AppSettings, PlaybackState, ScriptDocument } from "./types";

const DATABASE_NAME = "line-by-line";
const DATABASE_VERSION = 2;
const SCRIPT_STORE = "scripts";
const PLAYBACK_STORE = "playback";
const SETTINGS_STORE = "settings";

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => createMissingStores(request.result);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function createMissingStores(database: IDBDatabase): void {
  if (!database.objectStoreNames.contains(SCRIPT_STORE)) database.createObjectStore(SCRIPT_STORE, { keyPath: "id" });
  if (!database.objectStoreNames.contains(PLAYBACK_STORE)) database.createObjectStore(PLAYBACK_STORE, { keyPath: "scriptId" });
  if (!database.objectStoreNames.contains(SETTINGS_STORE)) database.createObjectStore(SETTINGS_STORE, { keyPath: "id" });
}

async function executeRequest<T>(storeName: string, mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const database = await openDatabase();
  return new Promise((resolve, reject) => {
    const transaction = database.transaction(storeName, mode);
    const request = operation(transaction.objectStore(storeName));
    let settled = false;
    const fail = (error: DOMException | null) => {
      if (settled) return;
      settled = true;
      database.close();
      reject(error ?? new Error(`IndexedDB ${mode} transaction failed for ${storeName}.`));
    };
    transaction.onerror = () => fail(transaction.error);
    transaction.onabort = () => fail(transaction.error);
    transaction.oncomplete = () => {
      if (settled) return;
      settled = true;
      database.close();
      resolve(request.result);
    };
  });
}

export const scriptRepository = {
  list: () => executeRequest<ScriptDocument[]>(SCRIPT_STORE, "readonly", (store) => store.getAll()),
  get: (id: string) => executeRequest<ScriptDocument | undefined>(SCRIPT_STORE, "readonly", (store) => store.get(id)),
  save: (script: ScriptDocument) => executeRequest<IDBValidKey>(SCRIPT_STORE, "readwrite", (store) => store.put({ ...script, updatedAt: new Date().toISOString() })),
  delete: (id: string) => executeRequest<undefined>(SCRIPT_STORE, "readwrite", (store) => store.delete(id)),
  clear: () => executeRequest<undefined>(SCRIPT_STORE, "readwrite", (store) => store.clear()),
};

export const playbackRepository = {
  list: () => executeRequest<PlaybackState[]>(PLAYBACK_STORE, "readonly", (store) => store.getAll()),
  get: (scriptId: string) => executeRequest<PlaybackState | undefined>(PLAYBACK_STORE, "readonly", (store) => store.get(scriptId)),
  save: (state: PlaybackState) => executeRequest<IDBValidKey>(PLAYBACK_STORE, "readwrite", (store) => store.put(state)),
  clear: () => executeRequest<undefined>(PLAYBACK_STORE, "readwrite", (store) => store.clear()),
};

export const settingsRepository = {
  async get(): Promise<AppSettings | undefined> {
    const record = await executeRequest<(AppSettings & { id: string }) | undefined>(SETTINGS_STORE, "readonly", (store) => store.get("app"));
    if (!record) return undefined;
    const { id: _storageKey, ...settings } = record;
    return settings;
  },
  save: (settings: AppSettings) => executeRequest<IDBValidKey>(SETTINGS_STORE, "readwrite", (store) => store.put({ id: "app", ...settings })),
};
